import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ArrowRight } from 'lucide-react';
import { STRIPE_CHECKOUT_URL } from '../constants/site';

const STORAGE_KEY = 'gate33-sticky-dismissed-v1';

// Next available live session — mirror the source of truth (Enrollment/Academy pages).
// Kept manually in sync — small enough to not require a shared data file.
const NEXT_LIVE_SESSION = '24 — 26 November 2026';

// Show the bar only on candidate funnel pages.
const CANDIDATE_ROUTES = ['/', '/academy', '/enrollment', '/operational-assessment', '/insights'];
// /mentorship deliberately excluded — mentorship page has its own hero CTA (Discovery Call).

const computeVisible = (pathname) => {
  if (typeof window === 'undefined') return false;
  try { if (localStorage.getItem(STORAGE_KEY)) return false; } catch (_) {}
  return CANDIDATE_ROUTES.some(
    (r) => pathname === r || (r !== '/' && pathname.startsWith(r))
  );
};

const StickyBar = () => {
  const location = useLocation();
  // Compute visibility synchronously on first render so the server-captured
  // snapshot and the client hydration tree match (avoids React error #418).
  const [visible, setVisible] = useState(() => computeVisible(location.pathname));

  useEffect(() => {
    setVisible(computeVisible(location.pathname));
  }, [location]);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  return (
    <div
      data-testid="sticky-bar"
      className="w-full bg-[#0F0F0F] text-[#FDFBF7] border-b border-[#C5A880]/40 relative z-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#C5A880] font-semibold flex-shrink-0">
          Next live session
        </span>
        <span className="text-xs sm:text-sm text-[#FDFBF7]">
          {NEXT_LIVE_SESSION} — limited seats
        </span>
        <a
          href={STRIPE_CHECKOUT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sticky-bar-reserve"
          onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'sticky_bar' })}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.16em] font-semibold text-[#C5A880] hover:text-[#FDFBF7] transition-colors"
        >
          Reserve <ArrowRight size={12} />
        </a>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="sticky-bar-dismiss"
        className="absolute top-1/2 right-3 md:right-6 -translate-y-1/2 text-[#9A9A9A] hover:text-[#FDFBF7] transition-colors p-1"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default StickyBar;
